import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon, FormattingService } from '@zag/ui';
import { type NotificationResponse } from '@zag/notifications/common';

/**
 * Shared notification item component used in both the bell panel and the full list.
 * Displays type icon, title, message (truncated), relative time and read/unread state.
 */
@Component({
  selector: 'zag-notification-item',
  imports: [TranslatePipe, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="notification-item"
      [class.notification-item--unread]="!notification().read"
      [class.notification-item--compact]="compact()"
      role="listitem"
    >
      <!-- Read/unread state icon -->
      <div class="notification-item__state-icon-wrap">
        @if (!notification().read) {
          <span class="notification-item__state-dot" [attr.aria-label]="'notifications.filters.unread' | translate"></span>
        } @else {
          <span class="notification-item__state-dot notification-item__state-dot--read"></span>
        }
      </div>

      <!-- Type icon -->
      <div class="notification-item__icon-wrap" [class]="'notification-item__icon-wrap--' + notification().type">
        <span [zagIcon]="typeIcon()" class="notification-item__icon"></span>
      </div>

      <!-- Content: title + time -->
      <div class="notification-item__content">
        <span class="notification-item__title">{{ notification().title | translate: translateParams() }}</span>
        <time class="notification-item__time" [attr.datetime]="notification().createdAt">
          {{ relativeTime() }}
        </time>
      </div>

      <!-- Actions -->
      @if (showActions() && !compact()) {
        <div class="notification-item__actions">
          @if (!notification().read) {
            <button
              type="button"
              class="notification-item__action-btn"
              [attr.aria-label]="'notifications.markAsRead' | translate"
              [attr.title]="'notifications.markAsRead' | translate"
              (click)="onMarkAsRead($event)"
            >
              <span zagIcon="done"></span>
            </button>
          }
          <button
            type="button"
            class="notification-item__action-btn"
            [attr.aria-label]="'notifications.archive' | translate"
            [attr.title]="'notifications.archive' | translate"
            (click)="onArchive($event)"
          >
            <span zagIcon="archive"></span>
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .notification-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        transition: box-shadow 0.15s ease;
        cursor: pointer;
        position: relative;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:hover {
          .notification-item__actions {
            opacity: 1;
          }
        }
      }

      .notification-item--unread {
        background: color-mix(in srgb, var(--color-primary) 4%, var(--surface-color-default));

        .notification-item__title {
          font-weight: 700;
        }
      }

      .notification-item--compact {
        padding: var(--spacing-xs) var(--spacing-sm);
        border: none;
        border-radius: 0;
        background: transparent;
      }

      .notification-item__state-icon-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        flex-shrink: 0;
      }

      .notification-item__state-dot {
        display: block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--color-primary);
        flex-shrink: 0;
      }

      .notification-item__state-dot--read {
        background: transparent;
        border: 1px solid var(--border-color-default);
      }

      .notification-item__icon-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        flex-shrink: 0;
        background: var(--surface-color-secondary);
      }

      .notification-item__icon-wrap--info {
        background: color-mix(in srgb, var(--color-info, #3b82f6) 15%, transparent);
        color: var(--color-info, #3b82f6);
      }

      .notification-item__icon-wrap--success {
        background: color-mix(in srgb, var(--color-success) 15%, transparent);
        color: var(--color-success);
      }

      .notification-item__icon-wrap--warning {
        background: color-mix(in srgb, var(--color-warning) 15%, transparent);
        color: var(--color-warning);
      }

      .notification-item__icon-wrap--error {
        background: color-mix(in srgb, var(--color-error) 15%, transparent);
        color: var(--color-error);
      }

      .notification-item__icon {
        font-size: 18px;
      }

      .notification-item__content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-width: 0;
      }

      .notification-item__title {
        flex: 1;
        font-weight: 500;
        color: var(--text-color-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .notification-item__time {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .notification-item__actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        opacity: 0;
        transition: opacity var(--transition-duration-fast);
      }

      .notification-item__action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        span {
          font-size: 16px;
        }
      }
    `,
  ],
})
export class NotificationItemComponent {
  private readonly formattingService = inject(FormattingService);

  readonly notification = input.required<NotificationResponse>();
  /** Whether to show action buttons (mark as read, archive) */
  readonly showActions = input<boolean>(true);
  /** Compact mode for the bell panel */
  readonly compact = input<boolean>(false);

  readonly markAsRead = output<string>();
  readonly archive = output<string>();

  protected typeIcon(): string {
    switch (this.notification().type) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  }

  /**
   * Optional interpolation parameters for the title/message i18n keys —
   * dispatched under `metadata.translateParams` (e.g. the reviewer comment of
   * a rejected time-tracking day). Absent for plain static notifications.
   */
  protected translateParams(): Record<string, unknown> | undefined {
    const params = this.notification().metadata?.['translateParams'];
    return typeof params === 'object' && params !== null ? (params as Record<string, unknown>) : undefined;
  }

  protected relativeTime(): string {
    const date = new Date(this.notification().createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffSec < 60) return `${diffSec}s`;
    if (diffMin < 60) return `${diffMin}m`;
    if (diffH < 24) return `${diffH}h`;
    if (diffD < 7) return `${diffD}d`;
    return this.formattingService.formatDate(date);
  }

  protected onMarkAsRead(event: Event): void {
    event.stopPropagation();
    this.markAsRead.emit(this.notification().id);
  }

  protected onArchive(event: Event): void {
    event.stopPropagation();
    this.archive.emit(this.notification().id);
  }
}
